import React from "react";

import { faSpinnerThird } from "@fortawesome/pro-regular-svg-icons/faSpinnerThird";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import notification from "antd/es/notification";
import { addDays, addWeeks, endOfDay, endOfToday, startOfDay, startOfToday } from "date-fns";
import { defineMessages } from "react-intl";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { RouteTravelMode } from "@mapmycustomers/shared/enum/routes/RouteTravelMode";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import {
  Activity,
  EntitiesSupportingRoutes,
  EntityTypesSupportingRoutes,
} from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Route, {
  CreateRoutePayload,
  CreateRouteStopItem,
  RoutePreview,
  RouteStopBulkItem,
} from "@mapmycustomers/shared/types/entity/Route";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import {
  Condition,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import getSuccessNotificationNode from "@app/component/createEditEntity/util/getSuccessNotificationNode";
import i18nService from "@app/config/I18nService";
import DateRangeType from "@app/scene/activity/component/ActivityCalendarPage/components/Sidebar/RouteBuilder/enum/DateRangeType";
import { getActivityTypes } from "@app/store/activity";
import { callApi } from "@app/store/api/callApi";
import { hideEntityView, openEntity } from "@app/store/entityView/actions";
import { showEntityChannel } from "@app/store/entityView/sagas";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUser, getOrganizationId } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import { NOTIFICATION_DURATION_WITH_ACTION } from "@app/util/consts";
import { getLocalTimeZoneFormattedOffset, getWeekEnd, getWeekStart } from "@app/util/dates";
import { formatDate } from "@app/util/formatters";
import { activityLayoutModel } from "@app/util/layout/impl";
import getRoutePreviewMetrics from "@app/util/route/routePreviewMetrics";

import {
  createRoute,
  initializeRouteBuilder,
  setActivities,
  setActivityTypes,
  setDateRange,
  setEntityType,
  updateActivityAssociation,
} from "./actions";
import { RouteBuilderState } from "./index";
import styles from "./saga.module.scss";
import {
  getEntityType,
  getInvalidStops,
  getRange,
  getRangeType,
  getSelectedActivityTypes,
  getStops,
} from "./selectors";

const messages = defineMessages({
  buildingRoute: {
    id: "activities.calendarView.routeBuilder.buildingRoute",
    defaultMessage: "Building route...",
    description: "message for build route when creating new route",
  },
  failedGeocodeEndAddress: {
    id: "activities.calendarView.routeBuilder.error.failedGeocodeEnd",
    defaultMessage: "Failed to find ending location",
    description: "Failed to find ending location in Route Builder in Activities Calendar view",
  },
  failedGeocodeStartAddress: {
    id: "activities.calendarView.routeBuilder.error.failedGeocodeStart",
    defaultMessage: "Failed to find starting location",
    description: "Failed to find starting location in Route Builder in Activities Calendar view",
  },
  success: {
    id: "activities.calendarView.routeBuilder.success",
    defaultMessage: "Route built successfully",
    description: "Route is updated successfully in Route Builder in Activities Calendar view",
  },
});

const getRangeCondition = (
  rangeType: DateRangeType,
  range?: { end: Date; start: Date }
): Condition | undefined => {
  switch (rangeType) {
    case DateRangeType.CUSTOM:
      return range ? { $gte: startOfDay(range.start), $lte: endOfDay(range.end) } : undefined;
    case DateRangeType.NEXT_WEEK:
      return {
        $gte: addWeeks(getWeekStart(Date.now()), 1),
        $lte: addWeeks(getWeekEnd(Date.now()), 1),
      };
    case DateRangeType.THIS_WEEK:
      return { $gte: getWeekStart(Date.now()), $lte: getWeekEnd(Date.now()) };
    case DateRangeType.TODAY:
      return { $gte: startOfToday(), $lte: endOfToday() };
    case DateRangeType.TOMORROW:
      return { $gte: addDays(startOfToday(), 1), $lte: addDays(endOfToday(), 1) };
  }
};

const getActivityDateRangeFilter = (
  rangeType: DateRangeType,
  range?: { end: Date; start: Date }
): PlatformFilterCondition | undefined => {
  const rangeCondition = getRangeCondition(rangeType, range);
  if (!rangeCondition) {
    return undefined;
  }
  // prepare for fetching, convert dates to strings
  rangeCondition.$gte = formatDate(rangeCondition.$gte! as Date, "yyyy-MM-dd");
  rangeCondition.$lte = formatDate(rangeCondition.$lte! as Date, "yyyy-MM-dd");
  // $offset is a valid param used for date filtering, but it's not an operator
  // @ts-ignore
  rangeCondition.$offset = getLocalTimeZoneFormattedOffset();
  return { startAt: rangeCondition };
};

export function* onInitialize() {
  const activityTypes: ActivityType[] = yield select(getActivityTypes);
  yield put(initializeRouteBuilder.success(activityTypes));
}

export function* onFetchActivities() {
  try {
    const dateRangeType: DateRangeType = yield select(getRangeType);
    const dateRange: RouteBuilderState["dateRange"] = yield select(getRange);
    const activityTypes: ActivityType[] = yield select(getSelectedActivityTypes);

    const rangeFilter = getActivityDateRangeFilter(dateRangeType, dateRange);
    if (!rangeFilter) {
      yield put(setActivities(undefined));
      return; // can't even fetch
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUser: User = yield select(getCurrentUser);

    const response: ListResponse<Activity> = yield callApi("fetchActivities", orgId, {
      $filters: {
        $and: [
          rangeFilter,
          { assigneeId: currentUser.id },
          { completed: false },
          ...(activityTypes.length
            ? [{ crmActivityTypeId: { $in: activityTypes.map(({ id }) => id) } }]
            : []),
        ],
        includeAccessStatus: true,
      },
      $limit: 1000,
      $order: "startAt",
    });

    yield put(setActivities({ activities: response.data, total: response.total }));
  } catch (error) {
    yield put(setActivities(undefined));
    yield put(handleError({ error }));
  }
}

export function* onUpdateStops({ payload }: ReturnType<typeof notifyAboutChanges>) {
  const invalidStops: Activity[] = yield select(getInvalidStops);
  const entityType: EntityTypesSupportingRoutes = yield select(getEntityType);

  // if updated any of activities from stops
  // or if updated any of entities from stops
  // then reload all stops
  if (payload.entityType === EntityType.ACTIVITY) {
    const activityIds = new Set(invalidStops.map(({ id }) => id));
    if ((payload.updated ?? []).some((activity) => activityIds.has(activity.id))) {
      yield call(onFetchActivities);
    }
  } else if (payload.entityType === entityType) {
    const entityIds = new Set(
      invalidStops
        .map(({ account, contact }) =>
          entityType === EntityType.COMPANY ? account?.id : contact?.id
        )
        .filter(isDefined)
    );
    if (!(payload.updated ?? []).every((entity) => !entityIds.has(entity.id))) {
      yield call(onFetchActivities);
    }
  }
}

export function* onUpdateActivityAssociation({
  payload,
}: ReturnType<typeof updateActivityAssociation.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const layoutId = activityLayoutModel.getLayoutFor(payload.activity).id;
    const { files, ...activity } = payload.activity; // excluding files from payload

    yield callApi("updateActivity", orgId, layoutId, {
      ...activity,
      ...(payload.entityType === EntityType.COMPANY
        ? { account: { id: payload.entityId } }
        : { contact: { id: payload.entityId } }),
    });
    yield put(updateActivityAssociation.success());
    yield call(onFetchActivities);
  } catch (error) {
    yield put(updateActivityAssociation.failure());
    yield put(handleError({ error }));
  }
}

export function* keepZendeskButtonHidden() {
  // yield call(setTimeout, () => window.zE("webWidget", "hide"), 0);
}

export function* onCreateRoute({ payload }: ReturnType<typeof createRoute.request>) {
  const key = `createRouteNotification_${payload.route.name}`;
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const intl = i18nService.getIntl();
    const { callback, route } = payload;

    let startPoint: GeocodeResult;
    try {
      startPoint = yield callApi("geocodeAddress", orgId, {
        address: route.routeDetail.startAddress,
      });
    } catch {
      notification.error({ message: intl?.formatMessage(messages.failedGeocodeStartAddress) });
      yield put(createRoute.failure());
      return;
    }

    let endPoint: GeocodeResult | undefined;
    if (route.routeDetail.endAddress) {
      try {
        endPoint = yield callApi("geocodeAddress", orgId, {
          address: route.routeDetail.endAddress,
        });
      } catch {
        notification.error({ message: intl?.formatMessage(messages.failedGeocodeEndAddress) });
        yield put(createRoute.failure());
        return;
      }
    }

    const stops: Activity[] = yield select(getStops);
    const entityIds: EntitiesSupportingRoutes["id"][] = stops.map(({ account, contact }) =>
      route.entityType === EntityType.COMPANY ? account!.id : contact!.id
    );

    const travelMode = route.routeDetail.travelMode ?? RouteTravelMode.DRIVE;
    const createRouteDetail: CreateRoutePayload["routeDetail"] = {
      allottedTime: route.routeDetail.allottedTime ?? 0,
      startAddress: route.routeDetail.startAddress!,
      startAt: route.routeDetail.startAt,
      startGeoPoint: startPoint.geoPoint!,
      travelMode,
      type: route.routeDetail.type,
      ...(endPoint?.geoPoint && route.routeDetail.endAddress
        ? { endAddress: route.routeDetail.endAddress, endGeoPoint: endPoint.geoPoint }
        : {}),
    };

    const stopAllottedTime = route.routeDetail.allottedTime ?? 0;
    const stopTimeItems: CreateRouteStopItem[] =
      route.entityType === EntityType.COMPANY
        ? entityIds.map((id) => ({ accountId: id, allottedTime: stopAllottedTime }))
        : entityIds.map((id) => ({ allottedTime: stopAllottedTime, contactId: id }));

    notification.info({
      icon: <FontAwesomeIcon className={styles.notificationLoader} icon={faSpinnerThird} spin />,
      key,
      message: intl?.formatMessage(messages.buildingRoute),
    });

    const preview: RoutePreview = yield callApi("previewUnifiedRoute", orgId, {
      routeDetail: createRouteDetail,
      stopTime: stopTimeItems,
    });

    const createdRoute: Route = yield callApi("createUnifiedRoute", orgId, {
      name: route.name,
      routeDetail: createRouteDetail,
    } satisfies CreateRoutePayload);

    const appendOps: RouteStopBulkItem[] =
      route.entityType === EntityType.COMPANY
        ? entityIds.map((id) => ({
            _action_: "c",
            accountId: id,
            allottedTime: stopAllottedTime,
          }))
        : entityIds.map((id) => ({
            _action_: "c",
            allottedTime: stopAllottedTime,
            contactId: id,
          }));

    yield callApi(
      "bulkUpdateUnifiedRouteStops",
      orgId,
      createdRoute.id,
      appendOps,
      getRoutePreviewMetrics(preview)
    );

    notification.success({
      duration: NOTIFICATION_DURATION_WITH_ACTION,
      key: key,
      message: getSuccessNotificationNode(
        intl,
        i18nService.formatMessage(messages.success, "Route built successfully"),
        () => {
          showEntityChannel.put(
            openEntity({ entityId: createdRoute.id, entityType: EntityType.ROUTE })
          );
          notification.close(key);
        }
      ),
    });

    yield put(createRoute.success(createdRoute));
    callback?.();
  } catch (error) {
    notification.close(key);
    yield put(createRoute.failure());
    yield put(handleError({ error }));
  }
}

export function* routeBuilderSaga() {
  yield takeEvery(initializeRouteBuilder.request, onInitialize);
  yield takeLatest(
    [initializeRouteBuilder.request, setEntityType, setDateRange, setActivityTypes],
    onFetchActivities
  );
  yield takeEvery(notifyAboutChanges, onUpdateStops);
  yield takeEvery(updateActivityAssociation.request, onUpdateActivityAssociation);
  yield takeEvery(hideEntityView, keepZendeskButtonHidden);
  yield takeEvery(createRoute.request, onCreateRoute);
}
